import React, { useEffect, useState, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Send, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export default function ComplaintMessages({ complaintId }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const r = await axios.get(`${API}/complaints/${complaintId}/messages`, { withCredentials: true });
      setMessages(r.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, [complaintId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/complaints/${complaintId}/messages`, { complaint_id: complaintId, sender_id: user.id, sender_name: user.name, sender_role: user.role, body: text }, { withCredentials: true });
      setText('');
      fetchMessages();
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  return (
    <div data-testid="complaint-messages" className="border-t pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-teal-600" />
        <h4 className="font-semibold text-foreground text-sm">Messages</h4>
        <span className="text-xs text-muted-foreground">{messages.length}</span>
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2 mb-3 bg-slate-50 rounded-lg p-3">
        {loading ? <p className="text-xs text-muted-foreground text-center">Loading…</p> :
          messages.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No messages yet — start the conversation</p> :
          messages.map(m => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200'}`}>
                  <div className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${mine ? 'text-teal-100' : 'text-slate-500'}`}>
                    {m.sender_name} · {m.sender_role}
                  </div>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <div className={`text-[9px] mt-1 ${mine ? 'text-teal-100' : 'text-slate-400'}`}>{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })
        }
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <Textarea
          data-testid="message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message to management..."
          rows={2}
          className="flex-1"
        />
        <Button data-testid="send-message-btn" onClick={send} disabled={!text.trim() || sending} className="bg-teal-600 hover:bg-teal-700">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
